import React from 'react';
import {
  Box,
  Stack,
  Typography,
  Select,
  Option,
  Input,
  Chip,
  Divider,
} from '@mui/joy';
import ArticleRoundedIcon from '@mui/icons-material/ArticleRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { ExpandMore, ExpandLess, StackedLineChartRounded } from '@mui/icons-material';
import { CustomDimensions, FeatureColor } from 'styles/theme/themes';
import { EXAMLEVEL, EXAMSUBJECT } from 'app/constants/content';
import { TypeSortByExam } from '../../utils/types';

type ExamHeaderProps = {
  upcomingExam?: boolean;
  subject: string;
  level: string;
  searchQuery: string;
  sortBy: TypeSortByExam;
  isExpanded: boolean;
  statusFilter: string[];
  setSubject: (value: string) => void;
  setPaper: (value: string) => void;
  setLevel: (value: string) => void;
  setSearchQuery: (value: string) => void;
  setSortBy: (value: TypeSortByExam) => void;
  setIsExpanded: (value: boolean) => void;
  setStatusFilter: (value: string[]) => void;
  userCount?: number;
  showUserCount?: boolean;
};

export function ExamHeader({
  upcomingExam,
  subject,
  level,
  searchQuery,
  sortBy,
  isExpanded,
  statusFilter,
  setSubject,
  setPaper,
  setLevel,
  setSearchQuery,
  setSortBy,
  setIsExpanded,
  setStatusFilter,
  userCount,
  showUserCount,
}: ExamHeaderProps) {
  console.log("🚀 ~ file: ExamHeader.tsx ~ line 56 ~ statusFilter", statusFilter)
  return (
    <Stack mb={2}>
      <Stack
        direction={{ xs: 'column', md: "row" }}
        alignItems="center"
        sx={{ mb: 3, display: upcomingExam ? 'none' : undefined, gap: 2, alignItems: { xs: 'start', md: 'center' } }}
        justifyContent={'space-between'}
      >
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="start" width="100%">
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: CustomDimensions.cornerRadius,
              backgroundColor: FeatureColor.mockGen[200],
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ArticleRoundedIcon sx={{ fontSize: 32, color: 'grey.400' }} />
          </Box>
          <Typography level="h2">{upcomingExam ? 'Upcoming Exams' : 'Exam'}</Typography>
        </Stack>
        {showUserCount && (
          <Chip
            size="md"
            variant='outlined'
            color="primary"
            sx={{
              bgcolor: 'background.primary',
              padding: 2,
              borderRadius: 16,
            }}
          >
            Registered Users : {userCount}
          </Chip>
        )}
      </Stack>

      <Stack spacing={2}>
        <Stack direction={{ xs: 'column', md: "row" }} sx={{ display: upcomingExam ? 'none' : undefined, gap: 2 }}>
          <Select
            placeholder="Subject"
            value={subject}
            onChange={(_, value) => {
              setSubject(value as string);
              setPaper('');
            }}
            sx={{ width: { xs: 1, md: 200 } }}
          >
            <Option key={'all'} value={'all'}>
              All Subject
            </Option>
            {Object.keys(EXAMSUBJECT).map(subject => (
              <Option key={subject} value={subject}>
                {EXAMSUBJECT[subject]}
              </Option>
            ))}
          </Select>

          <Select
            placeholder="Level"
            value={level}
            defaultValue={'all'}
            onChange={(_, value) => {
              setLevel(value as string);
            }}
            sx={{ width: { xs: 1, md: 200 } }}
          >
            <Option key={'all'} value={'all'}>
              All Level
            </Option>
            {Object.keys(EXAMLEVEL).map(l => (
              <Option key={l} value={l}>
                {EXAMLEVEL[l]}
              </Option>
            ))}
          </Select>

          <Input
            placeholder="Search exam..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            startDecorator={<SearchRoundedIcon />}
            sx={{ flexGrow: 1 }}
          />
        </Stack>

        {/* Filter and Sort Controls */}
        <Stack
          spacing={2}
          sx={{
            display: upcomingExam ? 'none' : 'flex',
          }}
        >
          {/* Status Filter */}
          <Stack direction="column" spacing={1}>
            <Typography level="body-sm" fontWeight="lg">Filter by Status:</Typography>
            <Stack
              direction="row"
              sx={{
                flexWrap: 'wrap',
                gap: 1
              }}
            >
              <Chip
                variant={statusFilter.length === 0 ? 'solid' : 'outlined'}
                color={statusFilter.length === 0 ? 'primary' : 'neutral'}
                onClick={() => setStatusFilter([])}
                sx={{ cursor: 'pointer' }}
              >
                All
              </Chip>
              <Chip
                variant={statusFilter.includes('open') ? 'solid' : 'outlined'}
                color={statusFilter.includes('open') ? 'primary' : 'neutral'}
                onClick={() => {
                  setStatusFilter(
                    statusFilter.includes('open')
                      ? statusFilter.filter(s => s !== 'open')
                      : [...statusFilter, 'open']
                  );
                }}
                sx={{
                  cursor: 'pointer',
                  color: statusFilter.includes('open') ? '' : '#FBB117',
                  button: {
                    borderColor: '#FBB117',
                    ...(statusFilter.includes('open') && {
                      backgroundColor: '#FBB117',
                      borderColor: '#FBB117',
                      '&:hover': {
                        backgroundColor: '#FBB117',
                        borderColor: '#FBB117',
                      }
                    })

                  }
                }}
              >
                Enroll Now
              </Chip>
              <Chip
                variant={statusFilter.includes('standby') ? 'solid' : 'outlined'}
                color="warning"
                onClick={() => {
                  setStatusFilter(
                    statusFilter.includes('standby')
                      ? statusFilter.filter(s => s !== 'standby')
                      : [...statusFilter, 'standby']
                  );
                }}
                sx={{
                  cursor: 'pointer',
                  ...(statusFilter.includes('standby') && {
                    backgroundColor: '#F4B837',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: '#F4B837',
                    }
                  })
                }}
              >
                Stand By
              </Chip>
              <Chip
                variant={statusFilter.includes('countdown') ? 'solid' : 'outlined'}
                color="warning"
                onClick={() => {
                  setStatusFilter(
                    statusFilter.includes('countdown')
                      ? statusFilter.filter(s => s !== 'countdown')
                      : [...statusFilter, 'countdown']
                  );
                }}
                sx={{
                  cursor: 'pointer',
                  ...(statusFilter.includes('countdown') && {
                    backgroundColor: '#ff9a00',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: '#ff9a00',
                    }
                  })
                }}
              >
                Countdown
              </Chip>
              <Chip
                variant={statusFilter.includes('ongoing') ? 'solid' : 'outlined'}
                color="primary"
                onClick={() => {
                  setStatusFilter(
                    statusFilter.includes('ongoing')
                      ? statusFilter.filter(s => s !== 'ongoing')
                      : [...statusFilter, 'ongoing']
                  );
                }}
                sx={{
                  cursor: 'pointer'
                }}
              >
                On Going
              </Chip>
              <Chip
                variant={statusFilter.includes('submitted') ? 'soft' : 'outlined'}
                color="warning"
                onClick={() => {
                  setStatusFilter(
                    statusFilter.includes('submitted')
                      ? statusFilter.filter(s => s !== 'submitted')
                      : [...statusFilter, 'submitted']
                  );
                }}
                sx={{
                  cursor: 'pointer'
                }}
              >
                Submitted
              </Chip>
              <Chip
                variant={statusFilter.includes('graded') ? 'solid' : 'outlined'}
                color="success"
                onClick={() => {
                  setStatusFilter(
                    statusFilter.includes('graded')
                      ? statusFilter.filter(s => s !== 'graded')
                      : [...statusFilter, 'graded']
                  );
                }}
                sx={{
                  cursor: 'pointer',
                  ...(statusFilter.includes('graded') && {
                    backgroundColor: '#29B14E',
                    '&:hover': {
                      backgroundColor: '#29B14E',
                    }
                  })
                }}
              >
                Graded
              </Chip>
              <Chip
                variant={statusFilter.includes('close') ? 'soft' : 'outlined'}
                color="neutral"
                onClick={() => {
                  setStatusFilter(
                    statusFilter.includes('close')
                      ? statusFilter.filter(s => s !== 'close')
                      : [...statusFilter, 'close']
                  );
                }}
                sx={{
                  cursor: 'pointer'
                }}
              >
                Closed
              </Chip>
            </Stack>
          </Stack>

          <Divider />

          <Stack direction="row" justifyContent="flex-end" spacing={2} alignItems={'center'}>
            <Typography level="body-sm">Sorted by:</Typography>
            <Select
              placeholder="Sort by"
              value={sortBy}
              onChange={(_, value) => setSortBy(value as TypeSortByExam)}
              sx={{
                width: 120,
                borderWidth: 0,
                boxShadow: 'none',
                backgroundColor: 'transparent',
              }}
              size="sm"
              indicator={isExpanded ? <ExpandLess /> : <ExpandMore />}
              onListboxOpenChange={isOpen => setIsExpanded(isOpen)}
            >
              <Option value="examTime">Exam Date</Option>
              <Option value="title">Name</Option>
              <Option value="subject">Subject</Option>
              <Option value="status">Status</Option>
              <Option value="level">Level</Option>
            </Select>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
}
